import { ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs/operators';
import { AppComponentBase } from '@shared/app-component-base';
import { ArquivoServiceProxy, AssinaturaDto, AssinaturaServiceProxy, PlanoDto, PublicoAlvoEnum, TipoAssinaturaEnum } from '@shared/service-proxies/service-proxies';
import { ViewSituacaoPagamentoDialogComponent } from '@shared/views/view-situacao-pagamento/view-situacao-pagamento-dialog.component';
import * as moment from 'moment-timezone';
import { HistoricoAssinaturaComponent } from './historico-assinatura/historico-assinatura.component';
import { HistoricoCobrancaCustoAdicionalComponent } from './historico-cobranca-custo-adicional/historico-cobranca-custo-adicional.component';
import { StringUtils } from '@shared/helpers/StringUtils';

@Component({
  selector: 'app-minha-assinatura',
  templateUrl: './minha-assinatura.component.html',
  styleUrls: ['./minha-assinatura.component.scss']
})
export class MinhaAssinaturaComponent extends AppComponentBase implements OnInit {

  @Input() publico: PublicoAlvoEnum;
  protected showPlanos: boolean = false;
  protected assinatura: AssinaturaDto = undefined;
  protected plano: PlanoDto = undefined;
  protected dataAssinatura: string;
  protected dataEncerramento: string;
  protected espacoUsado: number = 0;

  constructor(
    injector: Injector,
    private readonly _arquivoService: ArquivoServiceProxy,
    private readonly _assinaturaService: AssinaturaServiceProxy,
    private _modalService: BsModalService,
    private _cdr: ChangeDetectorRef
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getAssinatura();
    this.getEspacoUtilizado();
  }

  private getEspacoUtilizado(): void {
    if (this.publico == PublicoAlvoEnum._0) return;
    abp.ui.setBusy();
    this._arquivoService.getTamanhoArquivos(this.appSession.pacienteId, this.appSession.userId).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((result) => {
      this.appSession.updateEspacoUtilizado(result);
      this.espacoUsado = this.appSession.getEspacoUtilizado();
    });
  }

  private getAssinatura(): void {
    abp.ui.setBusy();
    this._assinaturaService.obterAssinatura(this.appSession.userId).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((result) => {
      if (result?.id && result?.plano) {
        this.assinatura = result;
        this.plano = this.assinatura.plano;
        this.dataAssinatura = this.assinatura?.dataAssinatura?.format("DD/MM/YYYY") ?? "";
        if (this.assinatura.dataEncerramento) {
          this.dataEncerramento = this.assinatura?.dataEncerramento.format("DD/MM/YYYY");
        }
        this._cdr.markForCheck();
      }
      this.showPlanos = true;
    });
  }

  protected getSituacao() {
    return StringUtils.getSituacao(this.assinatura.status)
  }

  protected vizualizarStatus() {
    this._modalService.show(
      ViewSituacaoPagamentoDialogComponent,
      {
        class: 'modal-lg',
        initialState: {
          userId: this.appSession.userId,
          assinatura: this.assinatura.clone()
        },
      }
    );
  }

  protected vizualizarHistorico() {
    this._modalService.show(
      HistoricoAssinaturaComponent,
      {
        class: 'modal-xl',
        ignoreBackdropClick: true,
      }
    );
  }

  protected vizualizarHistoricoCobrancaExtra(): void {
    this._modalService.show(
      HistoricoCobrancaCustoAdicionalComponent,
      {
        class: 'modal-xl',
        ignoreBackdropClick: true,
      }
    );
  }

  protected ehClinica(): boolean {
    return this.publico == PublicoAlvoEnum._0
  }

  protected cancelarAssinatura(): void {
    abp.message.confirm("Ao prosseguir, você deixará de contar com os recursos premium oferecidos pelo PRODIGIUS.", "Deseja mesmo cancelar sua assinatura?", (option) => {
      if (option) {
        abp.ui.setBusy();
        this._assinaturaService.cancelarAssinatura(this.assinatura.id, this.appSession.userId, this.publico).pipe(
          finalize(() => {
            abp.ui.clearBusy();
          })
        ).subscribe(() => {
          this.getAssinatura();
          abp.notify.success("Assinatura cancelada com sucesso!");
        })
      }
    })
  }

  protected validarAssinatura(): boolean {
    if (this.assinatura?.preapprovalId) return true;
    return this.assinatura?.dataEncerramento && this.assinatura?.dataEncerramento.isAfter(moment()) && this.assinatura?.status != 'cancelled';
  }

  protected getProximaCobranca(): string {
    if (this.assinatura.preapprovalId) {
      if (this.assinatura.tipoAssinatura == TipoAssinaturaEnum._0) {
        return this.assinatura.dataAssinatura.clone().add(1, 'year').format("DD/MM/YYYY");
      } else if (this.assinatura.tipoAssinatura == TipoAssinaturaEnum._1) {
        const dataOriginal = this.assinatura.dataAssinatura.clone();
        const diaOriginal = dataOriginal.date();
        const hoje = moment();

        let proximaData = moment().year(hoje.year()).month(hoje.month()).date(diaOriginal);

        if (!proximaData.isValid() || proximaData.date() !== diaOriginal) {
          proximaData = moment().year(hoje.year()).month(hoje.month()).endOf('month');
        }

        if (proximaData.isSame(hoje, 'day')) {
          proximaData.add(1, 'month');

          if (proximaData.date() !== diaOriginal) {
            proximaData = proximaData.clone().date(1).endOf('month');
          }
        }

        return proximaData.format("DD/MM/YYYY");
      }
    }

    return this.assinatura.dataEncerramento.format("DD/MM/YYYY");
  }
}
